<script setup lang="ts">
import EvangelistIcon from "../Icons/EvangelistIcon.vue";
import CampaignConnectedWallets from "./CampaignConnectedWallets.vue";
import { resolveWalletConnectorLabel } from "./campaignDisplayHelpers";
import type { Brand } from "~/advertisorsData/shared/types";
import { getBrand } from "~/advertisorsData/shared/getBrand";
import { CreatorappCampaignWeekUserEligibilityState as EligibilityState } from "evangelist-db-schema/api";
import { useQuery } from "@tanstack/vue-query";

const emit = defineEmits(["closeAndRefresh"]);

interface Props {
  campaignWeekId: string;
  seeCriteria?: boolean;
}
const props = defineProps<Props>();

const { $trpc } = useNuxtApp();
const {
  data,
  isLoading: isCampaignLoading,
  isSuccess: isCampaignLoaded,
  promise,
} = useQuery({
  queryKey: ["getCampaign", props.campaignWeekId],
  queryFn: () =>
    $trpc.getCampaign.query({ campaignWeekId: props.campaignWeekId }),
});
const campaign = computed(() => data.value?.campaignWeek);

const { data: wallets } = useQuery({
  queryKey: ["listCampaignWeekWallets", props.campaignWeekId],
  queryFn: () =>
    $trpc.listCampaignWeekWallets.query({
      campaignWeekId: props.campaignWeekId,
    }),
});

const walletList = computed(() => wallets.value ?? []);

const brand = ref<Brand>();

const isWalletInviteCampaign = computed(
  () => campaign.value?.requiresWalletVerification ?? false,
);

const hasApyBonus = computed(() => (campaign.value?.apyBonus ?? 0) > 0);

const hasAdditionalWalletChecks = computed(
  () => isWalletInviteCampaign.value || hasApyBonus.value,
);

const lastNonEligibleWalletAddress = computed(() => {
  if (!hasAdditionalWalletChecks.value) {
    return null;
  }
  if (!walletList.value.length) {
    return null;
  }
  for (const wallet of walletList.value) {
    // Only show as "non-eligible" if the wallet was verified but still not eligible
    // or if it's not invited at all
    if (
      !wallet.isEligible &&
      (wallet.walletState === "verified" || wallet.isInvited === false)
    ) {
      return wallet.address;
    }
  }
  return null;
});

const eligibilityRestrictions = computed(() => {
  if (!campaign.value) {
    return [];
  }

  const restrictions: string[] = [];
  if (campaign.value.restrictionAccountAgeDays) {
    if (campaign.value.restrictionAccountAgeDays === 365) {
      restrictions.push(`Account age is more than one year`);
    } else {
      restrictions.push(
        `Account age is more than ${campaign.value.restrictionAccountAgeDays} days`,
      );
    }
  }
  if (campaign.value.restrictionMinFollowers) {
    restrictions.push(
      `Has more than ${campaign.value.restrictionMinFollowers} followers`,
    );
  }

  return restrictions;
});

const walletConnectorLabel = computed(() =>
  resolveWalletConnectorLabel({
    campaign: campaign.value,
    lastNonEligibleWalletAddress: lastNonEligibleWalletAddress.value,
  }),
);

const filteredWallets = computed(() =>
  walletList.value.filter((wallet) =>
    wallet.caipAddress?.startsWith("eip155:"),
  ),
);

const shouldShowWalletList = computed(() => filteredWallets.value.length > 0);

const showCriteriaOnly = computed(() => props.seeCriteria === true);

const shouldShowWalletConnector = computed(
  () => isWalletInviteCampaign.value && !showCriteriaOnly.value,
);

const walletSectionHeadline = computed(() => {
  if (!isWalletInviteCampaign.value) {
    return null;
  }

  if (shouldShowWalletList.value) {
    return "Your connected wallets";
  }

  return "Connect your invited wallet";
});

function onWalletConnected() {
  // Just emit event when wallet is connected or removed
  console.log("Wallet connected/removed");
  globalEmit("walletSaved", props.campaignWeekId);
}

function onWalletVerified() {
  // This is called after verification flow completes
  // The CampaignConnectedWallets component handles navigation if eligible
  console.log("Wallet verification flow completed");
  globalEmit("walletSaved", props.campaignWeekId);
}

watch(
  () => [isCampaignLoaded.value, campaign.value] as const,
  ([isCampaignLoaded, campaign]) => {
    if (isCampaignLoaded && campaign) {
      getBrand(campaign.advertiser.theme).then((b) => (brand.value = b));
    }
  },
  { immediate: true },
);
</script>

<template>
  <LoadingIcon v-if="isCampaignLoading || !campaign" />
  <div v-else>
    <div class="text-lg mb-6 pr-10 lg:pr-0">
      <p class="text-gray-400">Creator eligibility criteria for:</p>
      <p>{{ campaign.title }}</p>
    </div>

    <section
      v-if="isWalletInviteCampaign"
      class="mb-8 space-y-4 rounded-2xl border border-[#D2CEFF] bg-[#F6F5FF] p-5"
    >
      <div class="space-y-2">
        <p v-if="walletSectionHeadline" class="font-semibold text-sm">
          {{ walletSectionHeadline }}
        </p>
        <p class="text-sm text-black/65">
          This campaign is invite-only. Connect the wallet address that has been
          invited to this campaign, then verify ownership to unlock
          participation and rewards. You can connect multiple wallets to find
          the one that is eligible.
        </p>
      </div>

      <CampaignConnectedWallets
        v-if="shouldShowWalletList"
        :campaign-week-id="campaign.campaignWeekId"
        :wallets="filteredWallets"
        @verified="onWalletVerified"
        @removed="onWalletConnected"
      />

      <p
        v-if="!shouldShowWalletList"
        class="rounded-xl bg-white px-4 py-3 text-sm text-black/60 shadow-sm"
      >
        No invited wallet connected yet. Use the button below to connect the
        wallet address from your invite.
      </p>

      <div v-if="shouldShowWalletConnector" class="flex flex-wrap gap-3">
        <WalletsWalletConnector
          class="inline-flex items-center gap-2 rounded-full bg-[#6659FF] px-[20px] py-[10px] font-semibold text-white hover:bg-[#4f47ff]"
          :campaign-week-id="campaign.campaignWeekId"
          :ethereum-only="true"
          @saved="onWalletConnected"
        >
          {{
            shouldShowWalletList
              ? "Connect another wallet"
              : walletConnectorLabel
          }}
        </WalletsWalletConnector>
        <a
          class="flex items-center text-sm font-semibold text-[#6659FF] hover:underline"
          href="mailto:support@evangelist.gg"
          >Need help?</a
        >
      </div>

      <p class="text-xs text-black/50 leading-relaxed">
        After connecting, select “Verify ownership” next to the wallet to sign a
        verification message and confirm you control the invited address.
      </p>
    </section>

    <div v-if="campaign.eligibilityState === EligibilityState.Ineligible">
      <div v-if="hasAdditionalWalletChecks && !showCriteriaOnly">
        <div class="text-black/50 flex items-center gap-3 flex-wrap">
          <BrandsIconAndName :theme="campaign?.advertiser?.theme" />
          <span class="w-1 h-1 rounded-full bg-black/50"></span>
          <CampaignDateInfo :campaign="campaign" />
          <span
            v-if="campaign.signupBonus > 0"
            class="w-1 h-1 rounded-full bg-black/50"
          ></span>
          <div
            v-if="campaign.apyBonus > 0"
            class="flex items-center gap-1 px-[8px] py-[3px] rounded-full text-xs bg-[#CFCBFF] text-black"
          >
            <IconsShieldIcon class="mr-[4px] text-black fill-black" />
            Verified Members
          </div>
          <div
            v-if="campaign.signupBonus > 0"
            class="flex items-center gap-1 px-[8px] py-[3px] rounded-full text-black text-xs bg-gray-300"
          >
            {{ campaign.signupBonus }} {{ campaign.budgetCurrency }} Sign up
            Bonus
          </div>
        </div>

        <div v-if="brand" class="mt-8">
          <p
            v-for="(criteriaDescription, i) of brand.eligibilityDescriptions[
              'not-eligible'
            ]"
            :key="i"
            :class="[i === 0 ? '' : 'mt-4']"
          >
            {{ criteriaDescription }}
          </p>
        </div>
      </div>
      <div v-if="!hasAdditionalWalletChecks || showCriteriaOnly">
        To join this campaign, advertiser set following criteria for creators to
        be eligible:

        <ul class="list-disc px-5 mt-6">
          <li
            v-for="(restriction, restrictionIndex) in eligibilityRestrictions"
            :key="restrictionIndex"
          >
            {{ restriction }}
          </li>
        </ul>
      </div>

      <a
        v-if="!hasAdditionalWalletChecks || seeCriteria"
        href="mailto: support@evangelist.gg"
        class="inline-block bg-[#CFCBFF] rounded-full px-[15px] py-[8px] mt-6"
      >
        Contact support
      </a>
    </div>

    <div
      v-if="campaign.eligibilityState === EligibilityState.Pending"
      class="text-md"
    >
      <div class="flex flax-wrap">
        <div class="text-black/50 flex items-center gap-3 flex-wrap">
          <BrandsIconAndName :theme="campaign?.advertiser?.theme" />
          <span class="w-1 h-1 rounded-full bg-black/50"></span>
          <CampaignDateInfo :campaign />
          <span
            v-if="campaign.signupBonus > 0"
            class="w-1 h-1 rounded-full bg-black/50"
          ></span>
          <div
            v-if="campaign.apyBonus > 0"
            class="flex items-center gap-1 px-[8px] py-[3px] rounded-full text-xs bg-[#CFCBFF] text-black"
          >
            <IconsShieldIcon class="mr-[4px] text-black fill-black" />
            Verified Members
          </div>
          <div
            v-if="campaign.signupBonus > 0"
            class="flex items-center gap-1 px-[8px] py-[3px] rounded-full text-black text-xs bg-gray-300"
          >
            {{ campaign.signupBonus }} {{ campaign.budgetCurrency }} Sign up
            Bonus
          </div>
        </div>
      </div>

      <div v-if="brand" class="mt-6">
        <p
          v-for="(criteriaDescription, i) of brand.eligibilityDescriptions
            .evaluating"
          :key="i"
          :class="[i === 0 ? '' : 'mt-4']"
        >
          {{ criteriaDescription }}
        </p>
      </div>

      <div class="flex gap-4 items-center mt-6">
        <button class="flex gap-2 bg-gray-200 rounded-full px-[15px] py-[12px]">
          <EvangelistIcon class="h-[20px] w-[20px] animate-spin" />
          <span>Verifying...</span>
        </button>
      </div>
    </div>
  </div>
</template>
